import { z } from 'zod';
import {
  IntellioAssistantDatasourceFamilySchema,
  IntellioAssistantPersonaLaneSchema,
  IntellioAssistantRuntimeAccessControlSchema,
  IntellioAssistantSubjectScopeSchema,
  type IntellioAssistantDatasourceFamily,
  type IntellioAssistantPersonaLane,
  type IntellioAssistantRuntimeAccessControl,
  type IntellioAssistantSubjectScope,
} from '../entities/assistant';

export const IntellioAssistantToolAuthorizationDeniedDatasourceSchema = z.object({
  datasourceFamily: IntellioAssistantDatasourceFamilySchema,
  reason: z.string().trim().min(1),
}).strict();

export const IntellioAssistantToolAuthorizationDecisionSchema = z.object({
  assistantPersonaLane: IntellioAssistantPersonaLaneSchema,
  capabilityId: z.string().trim().min(1).nullable().optional(),
  actionId: z.string().trim().min(1).nullable().optional(),
  allowed: z.boolean(),
  denialReasons: z.array(z.string().trim().min(1)).default([]),
  selectedDatasourceFamilies: z.array(IntellioAssistantDatasourceFamilySchema).default([]),
  deniedDatasourceFamilies: z.array(IntellioAssistantToolAuthorizationDeniedDatasourceSchema).default([]),
  subjectScopes: z.array(IntellioAssistantSubjectScopeSchema).default([]),
}).strict();
export type IntellioAssistantToolAuthorizationDecision = z.infer<
  typeof IntellioAssistantToolAuthorizationDecisionSchema
>;

export type IntellioAssistantToolAuthorizationSubjectRequirement = {
  kind: IntellioAssistantSubjectScope['kind'];
  ids?: readonly string[];
};

export type ResolveAssistantToolAuthorizationInput = {
  assistantPersonaLane: IntellioAssistantPersonaLane;
  actorGroups?: readonly string[];
  actorPermissions?: readonly string[];
  allowedPersonaLanes?: readonly IntellioAssistantPersonaLane[];
  requiredActorGroups?: readonly string[];
  requiredActorPermissions?: readonly string[];
  capabilityId?: string | null;
  actionId?: string | null;
  datasourceFamilies?: readonly IntellioAssistantDatasourceFamily[];
  subjectScopes?: readonly IntellioAssistantSubjectScope[];
  requiredSubjectScopes?: readonly IntellioAssistantToolAuthorizationSubjectRequirement[];
  runtimePolicy?: IntellioAssistantRuntimeAccessControl | null;
};

const normalizeToken = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const normalizeTokenSet = (values: readonly unknown[] | undefined): Set<string> => new Set(
  (values ?? [])
    .map(normalizeToken)
    .filter(Boolean),
);

const intersects = (left: Set<string>, right: Set<string>): boolean => (
  left.size === 0 || right.size === 0
    ? false
    : [...left].some((value) => right.has(value))
);

const isParentLaneDatasource = (family: IntellioAssistantDatasourceFamily): boolean => (
  family === 'parentPortal.records'
);

const hasRequiredSubjectScope = (
  scopes: readonly IntellioAssistantSubjectScope[],
  requirement: IntellioAssistantToolAuthorizationSubjectRequirement,
): boolean => scopes.some((scope) => {
  if (scope.kind !== requirement.kind) {
    return false;
  }
  const requiredIds = (requirement.ids ?? [])
    .map((id) => String(id ?? '').trim())
    .filter(Boolean);
  if (requiredIds.length === 0) {
    return scope.ids.length > 0 || scope.kind === 'actor-self';
  }
  return requiredIds.some((id) => scope.ids.includes(id));
});

export const resolveAssistantToolAuthorization = (
  input: ResolveAssistantToolAuthorizationInput,
): IntellioAssistantToolAuthorizationDecision => {
  const runtimePolicy = IntellioAssistantRuntimeAccessControlSchema.parse(input.runtimePolicy ?? {});
  const assistantPersonaLane = IntellioAssistantPersonaLaneSchema.parse(input.assistantPersonaLane);
  const actorGroups = normalizeTokenSet(input.actorGroups);
  const actorPermissions = normalizeTokenSet(input.actorPermissions);
  const requiredGroups = normalizeTokenSet(input.requiredActorGroups);
  const requiredPermissions = normalizeTokenSet(input.requiredActorPermissions);
  const allowedPersonaLanes = input.allowedPersonaLanes?.length
    ? new Set(input.allowedPersonaLanes)
    : null;
  const denialReasons: string[] = [];
  const datasourceFamilies = [...(input.datasourceFamilies ?? [])];
  const selectedDatasourceFamilies: IntellioAssistantDatasourceFamily[] = [];
  const deniedDatasourceFamilies: Array<{ datasourceFamily: IntellioAssistantDatasourceFamily; reason: string }> = [];
  const subjectScopes = [...(input.subjectScopes ?? [])];

  if (allowedPersonaLanes && !allowedPersonaLanes.has(assistantPersonaLane)) {
    denialReasons.push('persona_lane_not_allowed');
  }

  if (requiredGroups.size > 0 && !intersects(actorGroups, requiredGroups)) {
    denialReasons.push('actor_group_not_allowed');
  }

  if (requiredPermissions.size > 0 && !intersects(actorPermissions, requiredPermissions)) {
    denialReasons.push('actor_permission_not_allowed');
  }

  const capabilityId = String(input.capabilityId ?? '').trim() || null;
  if (capabilityId && runtimePolicy.disabledCapabilityIds.includes(capabilityId)) {
    denialReasons.push('capability_disabled');
  }

  const actionId = String(input.actionId ?? '').trim() || null;
  if (actionId && runtimePolicy.disabledActionIds.includes(actionId)) {
    denialReasons.push('action_disabled');
  }

  for (const family of datasourceFamilies) {
    if (assistantPersonaLane === 'parent-family' && !isParentLaneDatasource(family)) {
      deniedDatasourceFamilies.push({
        datasourceFamily: family,
        reason: 'parent_family_lane_cannot_use_staff_datasource',
      });
      continue;
    }
    if (runtimePolicy.disabledDatasourceFamilies.includes(family)) {
      deniedDatasourceFamilies.push({
        datasourceFamily: family,
        reason: 'datasource_disabled',
      });
      continue;
    }
    selectedDatasourceFamilies.push(family);
  }

  for (const requirement of input.requiredSubjectScopes ?? []) {
    if (!hasRequiredSubjectScope(subjectScopes, requirement)) {
      denialReasons.push(`missing_subject_scope:${requirement.kind}`);
    }
  }

  if (datasourceFamilies.length > 0 && selectedDatasourceFamilies.length === 0) {
    denialReasons.push('no_authorized_datasource');
  }

  for (const denied of deniedDatasourceFamilies) {
    if (!denialReasons.includes(denied.reason)) {
      denialReasons.push(denied.reason);
    }
  }

  return IntellioAssistantToolAuthorizationDecisionSchema.parse({
    assistantPersonaLane,
    capabilityId,
    actionId,
    allowed: denialReasons.length === 0,
    denialReasons,
    selectedDatasourceFamilies,
    deniedDatasourceFamilies,
    subjectScopes,
  });
};
